import { Close as CloseIcon } from "@mui/icons-material";
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { useToast } from "contexts/ToastContext";
import { usePatients } from "hooks/usePatients";
import React, { useEffect, useState } from "react";
import type { Patient } from "types/patient";
import { formatPatientName } from "utils/patient";

import { faxesApi } from "../../../apis";
import { useCelleraTheme } from "../../../theme/useCelleraTheme";
import type { FaxRecord } from "../../../types/fax";

interface AssignFaxToPatientModalProps {
  open: boolean;
  fax: FaxRecord | null;
  practiceId: string | null;
  onClose: () => void;
  onAssigned: () => void;
}

const AssignFaxToPatientModal: React.FC<AssignFaxToPatientModalProps> = ({
  open,
  fax,
  practiceId,
  onClose,
  onAssigned,
}) => {
  const { colors } = useCelleraTheme();
  const { showSuccess, showError } = useToast();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setPatient(null);
      setSearch("");
    }
  }, [open, fax]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Scope patient search to the active practice — prevents an admin viewing
  // Practice A's fax from assigning it to a patient in Practice B (HIPAA).
  const { patients, loading } = usePatients({
    page: 0,
    pageSize: 20,
    search: debouncedSearch || undefined,
    practiceId: practiceId ?? undefined,
  });

  const canSubmit = Boolean(patient && !submitting);

  const handleSubmit = async () => {
    if (!fax || !patient || !canSubmit) return;
    setSubmitting(true);
    try {
      await faxesApi.assignInboundFax({
        id: fax.id,
        practiceId: practiceId ?? undefined,
        assignInboundFaxDto: { patientId: patient.patientId },
      });
      showSuccess("Fax assigned to patient chart");
      onAssigned();
    } catch {
      showError("Failed to assign fax");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
        Assign to patient chart
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>
          You can help assign this fax file to a patient chart.
        </Typography>
        <Autocomplete<Patient>
          options={patients}
          value={patient}
          onChange={(_, value) => setPatient(value)}
          inputValue={search}
          onInputChange={(_, value) => setSearch(value)}
          getOptionLabel={(option) => formatPatientName(option)}
          isOptionEqualToValue={(a, b) => a.patientId === b.patientId}
          loading={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Patient"
              fullWidth
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={onClose} sx={{ borderRadius: 999 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          sx={{
            borderRadius: 999,
            backgroundColor: colors.primary.main,
            "&:hover": { backgroundColor: colors.primary.dark },
          }}
        >
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignFaxToPatientModal;
